// Deterministic (no AI/network) smart-search parser for the Events page.
// Turns short natural-language queries like "Techno Graz tomorrow" into the
// SAME filter fields the manual Filters panel already uses (city, genres,
// moods, date range) — this is intentionally not a second/parallel filter
// system. Anything left over after recognizing known keywords becomes a
// free-text term matched against name/venue/description/city/country.

const WEEKDAYS_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const WEEKDAYS_DE = ["sonntag", "montag", "dienstag", "mittwoch", "donnerstag", "freitag", "samstag"];
// Extra DE spelling seen in the wild for Saturday.
const WEEKDAY_ALIASES_DE = { sonnabend: 6 };

const TODAY_WORDS = ["today", "heute"];
const TOMORROW_WORDS = ["tomorrow", "morgen"];
const WEEKEND_WORDS = ["weekend", "wochenende"];

// YYYY-MM-DD in LOCAL time (never UTC) — matches the plain <input type="date">
// values already used for filterStartDate/filterEndDate and event.date.
const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// Next occurrence of a given weekday (0=Sunday..6=Saturday), today included
// if today already is that weekday — matches how people say "Friday" meaning
// "this coming Friday".
const nextWeekday = (targetDow, from) => {
  const diff = (targetDow - from.getDay() + 7) % 7;
  return addDays(from, diff);
};

const weekdayIndex = (word) => {
  const en = WEEKDAYS_EN.indexOf(word);
  if (en !== -1) return en;
  const de = WEEKDAYS_DE.indexOf(word);
  if (de !== -1) return de;
  if (word in WEEKDAY_ALIASES_DE) return WEEKDAY_ALIASES_DE[word];
  return -1;
};

// Explicit "DD.MM." or "DD.MM.YYYY" style date (common in DE/AT usage).
// When the year is omitted, picks the current year, or next year if that
// date has already passed — "a matching current/future year date".
const parseExplicitDate = (token, now) => {
  const match = token.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})?$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  let year = match[3] ? Number(match[3]) : now.getFullYear();
  let candidate = new Date(year, month - 1, day);
  if (!match[3] && candidate < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
    year += 1;
    candidate = new Date(year, month - 1, day);
  }
  return candidate;
};

// Case-insensitive helper: does `haystack` contain `needle` as a whole word
// or as one of its space-separated words (so "food" matches "Food Workshop").
const wordMatches = (optionLower, tokenLower) =>
  optionLower === tokenLower || optionLower.split(" ").includes(tokenLower);

/**
 * @param {string} query raw smart-search text
 * @param {object} opts
 * @param {string[]} opts.genreOptions canonical genre labels (config/eventOptions)
 * @param {string[]} opts.moodOptions canonical mood labels (config/eventOptions)
 * @param {string[]} opts.cityOptions known city names (from currently loaded events)
 * @param {Date} [opts.now] injectable for tests
 */
export function parseSmartSearch(query, { genreOptions = [], moodOptions = [], cityOptions = [], now = new Date() } = {}) {
  const result = { city: null, genres: [], moods: [], startDate: null, endDate: null, freeText: "" };
  const raw = (query || "").trim();
  if (!raw) return result;

  const tokens = raw.split(/\s+/);
  const leftover = [];

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  tokens.forEach((rawToken) => {
    const token = rawToken.toLowerCase();
    // Strip a single trailing period so "20.09." style tokens with extra
    // trailing punctuation ("Graz." at end of sentence) still match words.
    const bare = token.replace(/[.,;!?]+$/, "");

    const explicitDate = parseExplicitDate(rawToken, today);
    if (explicitDate) {
      const iso = toISODate(explicitDate);
      result.startDate = result.startDate || iso;
      result.endDate = iso;
      return;
    }
    if (TODAY_WORDS.includes(bare)) {
      const iso = toISODate(today);
      result.startDate = iso;
      result.endDate = iso;
      return;
    }
    if (TOMORROW_WORDS.includes(bare)) {
      const iso = toISODate(addDays(today, 1));
      result.startDate = iso;
      result.endDate = iso;
      return;
    }
    if (WEEKEND_WORDS.includes(bare)) {
      const saturday = nextWeekday(6, today);
      const sunday = addDays(saturday, 1);
      result.startDate = toISODate(saturday);
      result.endDate = toISODate(sunday);
      return;
    }
    const dow = weekdayIndex(bare);
    if (dow !== -1) {
      const iso = toISODate(nextWeekday(dow, today));
      result.startDate = iso;
      result.endDate = iso;
      return;
    }

    const genreMatch = genreOptions.find((g) => wordMatches(g.toLowerCase(), bare));
    if (genreMatch) {
      if (!result.genres.includes(genreMatch)) result.genres.push(genreMatch);
      return;
    }
    const moodMatch = moodOptions.find((m) => wordMatches(m.toLowerCase(), bare));
    if (moodMatch) {
      if (!result.moods.includes(moodMatch)) result.moods.push(moodMatch);
      return;
    }

    leftover.push(rawToken);
  });

  if (leftover.length > 0) {
    const leftoverPhrase = leftover.join(" ");
    const cityMatch = cityOptions.find(
      (c) => c.toLowerCase() === leftoverPhrase.toLowerCase() || leftover.some((w) => w.toLowerCase() === c.toLowerCase())
    );
    if (cityMatch) {
      result.city = cityMatch;
      const remaining = leftover.filter((w) => w.toLowerCase() !== cityMatch.toLowerCase());
      result.freeText = remaining.join(" ");
    } else if (leftover.length === 1) {
      // A single unrecognized word is almost always the intended city, even
      // if it isn't among currently-loaded events' cities yet.
      result.city = leftover[0];
    } else {
      result.freeText = leftoverPhrase;
    }
  }

  return result;
}
